import { supabase } from './supabase'

// Types
export type Platform = 'redm' | 'fivem' | 'both'

export interface DocPlugin {
  id: string
  created_at: string
  updated_at: string
  script_id: string | null
  slug: string
  name: string
  description: string | null
  logo_url: string | null
  platform: Platform
  is_active: boolean
  display_order: number
  linked_script?: {
    tebex_data: {
      image?: string
    } | null
  } | null
}

export interface DocSection {
  id: string
  created_at: string
  updated_at: string
  doc_plugin_id: string
  slug: string
  title: string
  icon_name: string | null
  display_order: number
  is_collapsed: boolean
}

export interface DocPage {
  id: string
  created_at: string
  updated_at: string
  doc_section_id: string
  slug: string
  title: string
  short_description: string | null
  content_md: string
  is_published: boolean
  display_order: number
  meta_title: string | null
  meta_description: string | null
}

export interface DocStructure {
  plugin: DocPlugin
  sections: Array<{
    id: string
    slug: string
    title: string
    icon_name: string | null
    display_order: number
    pages: Array<{
      id: string
      slug: string
      title: string
      short_description: string | null
      display_order: number
      icon_name: string | null
    }>
  }>
}

export interface DocSearchResult {
  page_id: string
  page_slug: string
  page_title: string
  section_slug: string
  section_title: string
  plugin_slug: string
  plugin_name: string
  snippet: string
  rank: number
  result_type: 'plugin' | 'page'
}

// API Functions

export async function getDocPlugins(): Promise<DocPlugin[]> {
  const { data, error } = await supabase
    .from('doc_plugins')
    .select(`
      *,
      linked_script:scripts!script_id(tebex_data)
    `)
    .eq('is_active', true)
    .order('display_order')

  if (error) throw error
  return data || []
}

export async function getDocPluginBySlug(slug: string): Promise<DocPlugin | null> {
  const { data, error } = await supabase
    .from('doc_plugins')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single()

  if (error) {
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

export async function getDocStructure(pluginSlug: string): Promise<DocStructure | null> {
  const { data, error } = await supabase.rpc('get_doc_structure', {
    p_plugin_slug: pluginSlug
  })

  if (error) throw error
  return data
}

export async function getDocPage(
  pluginSlug: string,
  sectionSlug: string,
  pageSlug: string
): Promise<DocPage | null> {
  // First get the plugin
  const plugin = await getDocPluginBySlug(pluginSlug)
  if (!plugin) return null

  // Get the section
  const { data: section, error: sectionError } = await supabase
    .from('doc_sections')
    .select('id')
    .eq('doc_plugin_id', plugin.id)
    .eq('slug', sectionSlug)
    .single()

  if (sectionError || !section) return null

  // Get the page
  const { data: page, error: pageError } = await supabase
    .from('doc_pages')
    .select('*')
    .eq('doc_section_id', section.id)
    .eq('slug', pageSlug)
    .eq('is_published', true)
    .single()

  if (pageError || !page) return null
  return page
}

export async function getFirstPage(pluginSlug: string): Promise<{
  sectionSlug: string
  pageSlug: string
} | null> {
  const structure = await getDocStructure(pluginSlug)
  if (!structure || !structure.sections.length) return null

  const firstSection = structure.sections[0]
  if (!firstSection.pages.length) return null

  return {
    sectionSlug: firstSection.slug,
    pageSlug: firstSection.pages[0].slug
  }
}

export async function searchDocs(
  query: string,
  pluginSlug?: string
): Promise<DocSearchResult[]> {
  const { data, error } = await supabase.rpc('search_docs', {
    query,
    p_plugin_slug: pluginSlug || null,
    result_limit: 20
  })

  if (error) throw error
  return data || []
}

